import React, { useState, useEffect, useMemo, useCallback, useLayoutEffect } from "react";
import { View, StyleSheet, TextInput, Pressable, Platform, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import useAuth from "@/hooks/useAuth";
import usePermissions from "@/hooks/usePermissions";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";
import { supabase } from "@/lib/supabase-client";
import useNavigationGuard from "@/hooks/useNavigationGuard";
import { useNavigation } from "@react-navigation/native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Table, ColumnDef } from "@/components/NewTable";
import { theme } from "@/constants/themes";
import Avatar from "@/components/Avatar";
// import { ArrayChips } from "@/components/Table/ArrayChips"; // TODO: Re-enable when array chip sizing fixed

// Type for shift user with position info
interface ShiftUserWithPosition {
  id: string;
  user_id: string;
  status: string;
  user: {
    id: string;
    first_name?: string;
    last_name?: string;
    preferred_name?: string;
    email_address?: string;
    status?: string;
    meta_data?: { markets?: string[] };
  };
  position?: {
    id: string;
    name: string;
  };
}

// Status color mapping
const STATUS_COLORS: Record<string, keyof Pick<theme, "complete" | "pending" | "attention" | "neutral">> = {
  CONFIRMED: "complete",
  ACCEPTED: "complete",
  INVITED: "pending",
  QUEUED: "neutral",
  DECLINED: "attention",
  CANCELLED: "attention",
  "NOT NEEDED": "neutral",
  "NO RESPONSE": "attention",
};

export function RelatedUsersScreen() {
  const { colors } = useTheme();
  const { displayNames } = useAuth();
  const { users: userPermissions } = usePermissions();
  const currentEntity = useCurrentEntity();
  const { pushNavigation } = useNavigationHistory();
  const navigation = useNavigation();
  const { navigateToEntity } = useNavigationGuard();
  const [users, setUsers] = useState<ShiftUserWithPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [marketMap, setMarketMap] = useState<Record<string, string>>({});
  const [searchValue, setSearchValue] = useState("");

  // Fetch markets for display mapping
  useEffect(() => {
    (async () => {
      const { data } = await supabase.schema("lists").from("markets").select("id, name");
      if (data) {
        setMarketMap(Object.fromEntries(data.map((m) => [m.id, m.name])));
      }
    })();
  }, []);

  const usersName = displayNames?.users?.plural?.toLowerCase() || "users";

  // Set up header with search
  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => (
        <View style={styles.headerTitleContainer}>
          <ThemedText style={styles.headerTitle}>{displayNames?.users?.plural || "Users"}</ThemedText>
          <View style={[styles.headerSearchContainer, { backgroundColor: colors.backgroundSecondary }]}>
            <MaterialCommunityIcons
              name="magnify"
              size={16}
              color={colors.textSecondary}
              style={styles.headerSearchIcon}
            />
            <TextInput
              value={searchValue}
              onChangeText={setSearchValue}
              placeholder="Search..."
              placeholderTextColor={colors.textSecondary}
              style={[styles.headerSearchInput, { color: colors.text }]}
              autoCapitalize="none"
              autoCorrect={false}
            />
            {searchValue.length > 0 && (
              <Pressable onPress={() => setSearchValue("")} style={styles.headerClearButton}>
                <MaterialCommunityIcons name="close-circle" size={14} color={colors.textSecondary} />
              </Pressable>
            )}
          </View>
        </View>
      ),
    });
  }, [navigation, searchValue, colors, displayNames?.users?.plural]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!currentEntity?.currentId || currentEntity?.currentPanel !== "shift") {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Step 1: Fetch shift_users with user data (no cross-schema position join)
        // Note: shift_users.id IS the user_id (FK to users.id)
        const { data: shiftUsersData, error: shiftUsersError } = await supabase
          .from("shift_users")
          .select(
            `
            id,
            shift_id,
            shift_position_id,
            status,
            user:users!shift_users_id_tenant_id_fkey (
              id,
              first_name,
              last_name,
              preferred_name,
              email_address,
              status,
              meta_data
            ),
            shift_position:shift_positions (
              id,
              position_id
            )
          `,
          )
          .eq("shift_id", currentEntity.currentId);

        if (shiftUsersError) throw shiftUsersError;

        // Step 2: Extract unique position_ids from shift_positions
        const positionIds = new Set<string>();
        shiftUsersData?.forEach((su: any) => {
          const positionId = su.shift_position?.position_id;
          if (positionId) {
            positionIds.add(positionId);
          }
        });

        // Step 3: Fetch positions from lists.positions schema
        let positionsMap = new Map<string, { id: string; name: string }>();
        if (positionIds.size > 0) {
          const { data: positionsData } = await supabase
            .schema("lists")
            .from("positions")
            .select("id, name")
            .in("id", Array.from(positionIds));

          if (positionsData) {
            positionsMap = new Map(positionsData.map((p) => [p.id, p]));
          }
        }

        // Step 4: Transform and enrich data with position names
        const transformedUsers: ShiftUserWithPosition[] = (shiftUsersData || [])
          .filter((item: any) => item.user) // Filter out any null users
          .map((item: any) => {
            const positionId = item.shift_position?.position_id;
            const position = positionId ? positionsMap.get(positionId) : undefined;

            return {
              id: item.id,
              user_id: item.id, // id IS the user_id
              status: item.status,
              user: item.user,
              position,
            };
          });

        setUsers(transformedUsers);
      } catch (err: any) {
        console.error("Failed to fetch related users:", err);
        setError(err.message || "Failed to load users");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [currentEntity?.currentId, currentEntity?.currentPanel]);

  const getStatusColor = (status?: string) => {
    const colorKey = STATUS_COLORS[status || "QUEUED"] || "neutral";
    return colors[colorKey].border;
  };

  const formatUserName = (user?: { first_name?: string; last_name?: string; preferred_name?: string } | null) => {
    if (!user) return null;
    const firstName = user.preferred_name || user.first_name || "";
    const lastName = user.last_name || "";
    return `${firstName} ${lastName}`.trim() || null;
  };

  const handleUserPress = useCallback(
    (shiftUser: ShiftUserWithPosition) => {
      // Save current entity to navigation history before navigating away
      if (currentEntity?.currentPanel && currentEntity?.currentId) {
        pushNavigation({
          panel: currentEntity.currentPanel,
          id: currentEntity.currentId,
          title: currentEntity.currentTitle || currentEntity.currentPanel,
        });
      }

      // Navigate to the user
      navigateToEntity("user", shiftUser.user_id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentEntity, pushNavigation],
  );

  // Define columns for the table
  const columns: ColumnDef<ShiftUserWithPosition>[] = useMemo(
    () => [
      {
        key: "avatar",
        header: "",
        width: 48,
        align: "center",
        sortable: false,
        searchable: false,
        render: (_, row) => (
          <Avatar
            userId={row.user_id}
            email={row.user?.email_address}
            firstName={row.user?.preferred_name || row.user?.first_name}
            lastName={row.user?.last_name}
            style={{ margin: -1 }}
            size={30}
          />
        ),
      },
      {
        key: "name",
        header: "Name",
        flex: 1,
        minWidth: 150,
        accessor: (row) => formatUserName(row.user) || "—",
        onPress: (row) => handleUserPress(row),
      },
      {
        key: "position",
        header: "Position",
        flex: 1,
        minWidth: 120,
        accessor: (row) => row.position?.name || "—",
      },
      // TODO: Fix sizing on array chip cell for NewTable
      // {
      //   key: "markets",
      //   header: "Markets",
      //   width: 180,
      //   searchable: true,
      //   searchValue: (_, row) => {
      //     const markets = (row.user?.meta_data as any)?.markets || [];
      //     return markets.map((m: string) => marketMap[m] || m).join(" ");
      //   },
      //   render: (_, row) => {
      //     const markets = (row.user?.meta_data as any)?.markets || [];
      //     if (markets.length === 0) return <ThemedText style={styles.dateText}>—</ThemedText>;
      //     return <ArrayChips values={markets} displayMap={marketMap} chipColor="#4169E1" maxDisplay={2} />;
      //   },
      // },
      {
        key: "status",
        header: "Status",
        width: 120,
        align: "center",
        searchable: true,
        searchValue: (_, row) => row.status || "QUEUED",
        render: (_, row) => (
          <View style={[styles.statusPill, { backgroundColor: getStatusColor(row.status) }]}>
            <ThemedText style={styles.statusText}>{row.status || "QUEUED"}</ThemedText>
          </View>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getStatusColor, handleUserPress, marketMap],
  );

  if (error) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="alert-circle" size={48} color={colors.red.text} />
          <ThemedText style={styles.emptyText}>Failed to load {usersName}</ThemedText>
          <ThemedText style={{ opacity: 0.6, fontSize: 12 }}>{error}</ThemedText>
        </View>
      </View>
    );
  }

  return (
    <ScrollView
      horizontal={true}
      showsHorizontalScrollIndicator={true}
      contentContainerStyle={{ width: "auto", flexGrow: 1 }}
      style={[styles.container, { backgroundColor: colors.background }]}
    >
      <Table<ShiftUserWithPosition>
        data={users}
        columns={columns}
        keyExtractor={(item) => item.id || ""}
        loading={loading}
        loadingRowCount={8}
        canEdit={userPermissions.update}
        canDelete={userPermissions.delete}
        searchValue={searchValue}
        sortable
        defaultSort={{ key: "name", direction: "asc" }}
        emptyMessage={`No ${usersName} found`}
        emptyIcon="account-group-outline"
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: "600",
  },
  headerSearchContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    minWidth: 140,
  },
  headerSearchIcon: {
    marginRight: 6,
  },
  headerSearchInput: {
    flex: 1,
    fontSize: 14,
    minWidth: 100,
    ...Platform.select({
      web: { outlineStyle: "none" as any },
    }),
  },
  headerClearButton: {
    padding: 2,
    marginLeft: 4,
  },
  statusPill: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  statusText: {
    fontSize: 10,
    fontWeight: "600",
    color: "white",
  },
  dateText: {
    fontSize: 12,
  },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
    gap: 8,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: "600",
    opacity: 0.6,
  },
});
